import { Alert, Input, Modal, Table } from "antd";
import React, { FC } from "react";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { Colors } from "shared/themes";
import { ModalSizeValue } from "shared/constants";
import { useDebouncedTableFilter, useProcessStepKeys } from "shared/hooks";
import { ColumnsType } from "antd/es/table";
import { FilterFilled } from "@ant-design/icons";
import { AdaptedValues } from "shared/types";
import { AddProductCardValues } from "../ProductCards";

interface ProcessStepToTheCardActionsModalProps {
  isOpenModal: boolean;
  hideProcessStepsModal: () => void;
  adaptedSteps: AdaptedValues[];
}

const ProcessStepToTheCardActionsModal: FC<
  ProcessStepToTheCardActionsModalProps
> = ({ isOpenModal, hideProcessStepsModal, adaptedSteps }) => {
  const { values, setFieldValue } = useFormikContext<AddProductCardValues>();

  const { t } = useTranslation();

  const {
    handleFilterChange: handleNameValue,
    value: nameValue,
    debouncedValue: debouncedNameValue,
  } = useDebouncedTableFilter("", 400);

  const {
    rowSelection,
    filteredAllSteps,
    filteredSelectedSteps,
    selectedProcessSteps,
    addProcessSteps,
    closeModal,
  } = useProcessStepKeys({
    processSteps: values?.process_steps as number[],
    adaptedSteps,
    setFieldValue,
    hideProcessStepsModal,
    // Temporary hardcoded to ''. Maybe it should be changed to a variable with filtering
    selectedNameValue: "",
    nameValue: debouncedNameValue,
  });

  const columns: ColumnsType<AdaptedValues> = [
    {
      title: t("product-card.process-step"),
      width: "100%",
      render: (record: AdaptedValues) => record?.label,
      filterIcon: (
        <FilterFilled
          className={nameValue.length > 0 ? "!text-green-100 scale-125" : ""}
        />
      ),
    },
  ];

  const selectedColumns: ColumnsType<AdaptedValues> = [
    {
      title: t("product-card.process-step"),
      width: "100%",
      render: (record: AdaptedValues) => record?.label,
    },
  ];

  return (
    <Modal
      width={ModalSizeValue.X_LARGE}
      open={isOpenModal}
      centered
      onCancel={closeModal}
      onOk={addProcessSteps}
      okButtonProps={{
        style: {
          background: `${Colors.blue.primary}`,
          color: "white",
        },
      }}
    >
      <div className="w-full flex gap-x-4">
        <div className="w-1/2">
          <Title level={5}>{t("product-card.all-process-steps")}</Title>

          <Input.Search
            placeholder={t("companies.search-product-card")!}
            value={nameValue}
            onChange={handleNameValue}
          />

          <Table
            rowSelection={{
              type: "checkbox",
              ...rowSelection,
            }}
            columns={columns}
            dataSource={filteredAllSteps}
            pagination={false}
            className="max-h-[820px] overflow-auto mt-3"
          />
        </div>

        <div className="w-1/2">
          <Title level={5}>{t("product-card.selected-process-steps")}</Title>

          {selectedProcessSteps?.length ? (
            <Table
              rowSelection={{
                type: "checkbox",
                ...rowSelection,
              }}
              columns={selectedColumns}
              dataSource={filteredSelectedSteps}
            />
          ) : (
            <Alert
              type="info"
              showIcon
              message={t("product-card.no-selected-process-steps")}
              className="w-full h-14"
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export { ProcessStepToTheCardActionsModal };
